/*----------------------------------
- DOMAINS LANDING SEO
----------------------------------*/

import type { WebsiteDomainsLandingResponse } from '@/common/types/domains/websiteLanding';
import { resolveDomainsRobotsDirectives } from '@/common/catalogs/crawlerPolicy';
import { buildDomainsStructuredData, type WebsiteJsonLdNode } from '@/client/pages/landing/seo/structuredData';
import { buildDomainsGeoContext, resolveDomainsGeoPathContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { resolveDomainsMarkdownMirrorPath } from './aiMarkdownMirror';
import { withCleanSearchSeoCopy } from './searchSeoCopy';
import { DOMAINS_PAGE_TITLE_FALLBACK } from '@/client/catalogs/landing/domains/pageCopy';
import { appErrorReporter } from '@/common/libs/errorReporting';
import {
    resolveWebsiteOpenGraphBanner,
    websiteOpenGraphBannerPublicPath,
} from '@/common/catalogs/landing/openGraphBanners';

/*----------------------------------
- TYPES
----------------------------------*/

export type DomainsLandingJsonLd = WebsiteJsonLdNode;

export type DomainsLandingSeo = {
    canonicalUrl: string;
    description: string;
    jsonld: DomainsLandingJsonLd[];
    metas: Record<string, string>;
    title: string;
};

/*----------------------------------
- HELPERS
----------------------------------*/

const toAbsoluteUrl = (inputUrl: string, requestUrl: string): string => {
    const safeRequestUrl = new URL(requestUrl, 'https://unique.domains').toString();

    try {
        return new URL(inputUrl, safeRequestUrl).toString();
    } catch (urlError) {
        appErrorReporter.app.handleError(urlError);
        console.warn('[domains-landing-seo] Failed to resolve canonical URL.', urlError);
        return new URL('/domains', safeRequestUrl).toString();
    }
};

/*----------------------------------
- EXPORTS
----------------------------------*/

export const buildDomainsLandingSeo = ({
    requestUrl,
    currentPath,
    search: storedSearch,
}: {
    requestUrl: string;
    currentPath: string;
    search: WebsiteDomainsLandingResponse | null;
}): DomainsLandingSeo => {
    // Cleaned here too, not only by the page route: the Markdown mirror route builds its SEO from
    // the raw resolution, and the title, metas and JSON-LD must never carry an em dash on either.
    const search = withCleanSearchSeoCopy(storedSearch);
    const canonicalUrl = toAbsoluteUrl(search?.url || currentPath || '/domains', requestUrl);
    const requestRouteUrl = new URL(requestUrl, canonicalUrl);
    const origin = new URL(canonicalUrl).origin;
    const socialImageUrl = new URL(
        websiteOpenGraphBannerPublicPath(resolveWebsiteOpenGraphBanner(requestRouteUrl.toString())),
        origin,
    ).toString();
    const previewRows = (search?.list || []).slice(0, 10);
    const title =
        search?.seo?.metadata?.title_tag?.trim() ||
        search?.seo?.page?.h1?.trim() ||
        search?.humanized?.trim() ||
        DOMAINS_PAGE_TITLE_FALLBACK;
    const description =
        search?.seo?.metadata?.meta_description?.trim() ||
        search?.seo?.page?.answer_snippet?.trim() ||
        'Explore one-word domains updated daily.';
    const ogTitle = search?.seo?.metadata?.og_title?.trim() || title;
    const ogDescription = search?.seo?.metadata?.og_description?.trim() || description;
    const faqs = search?.seo?.page?.faqs || [];
    const currentPathWithSearch = `${requestRouteUrl.pathname}${requestRouteUrl.search}`;
    const hasSearchFilters = Boolean(requestRouteUrl.search);
    const pathContext = resolveDomainsGeoPathContext(currentPathWithSearch, hasSearchFilters);
    const geoContext = buildDomainsGeoContext({
        currentPath: currentPathWithSearch,
        hasSearchFilters,
        markdownMirrorPath: resolveDomainsMarkdownMirrorPath(currentPathWithSearch),
        previewRows,
        searchHash: search?.searchId,
        searchSummary: search?.humanized || title,
        searchUrl: search?.url,
        ...(pathContext.sector ? { sector: pathContext.sector } : {}),
        ...(pathContext.tld ? { tld: pathContext.tld } : {}),
    });

    const metas: Record<string, string> = {
        robots: resolveDomainsRobotsDirectives(requestUrl),
        'og:image': socialImageUrl,
        'og:image:alt': ogTitle,
        'og:image:height': '630',
        'og:image:type': 'image/png',
        'og:image:width': '1200',
        'og:title': ogTitle,
        'og:description': ogDescription,
        'twitter:image': socialImageUrl,
        'twitter:image:alt': ogTitle,
        'twitter:title': ogTitle,
        'twitter:description': ogDescription,
    };

    const jsonld = buildDomainsStructuredData({
        canonicalUrl,
        currentPath: currentPathWithSearch,
        description,
        faqs,
        geoContext,
        previewRows,
        title,
    });

    return {
        canonicalUrl,
        description,
        jsonld,
        metas,
        title,
    };
};
